import React, {useEffect, useState} from 'react';
import {
    Button,
    Collapse,
    message,
    Modal,
    Popconfirm,
    Progress,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload
} from 'antd';
import {
    CheckCircleOutlined,
    DeleteOutlined,
    InboxOutlined,
    InfoCircleOutlined,
    ReloadOutlined,
    UploadOutlined,
    WarningOutlined
} from '@ant-design/icons';
import {useAtom} from 'jotai';
import {UploadFile} from 'antd/lib/upload';
import {batchItemsAtom, currentStepAtom, uploadedFilesAtom} from './types';
import {stripConsecutiveSpacesInFilename} from '../fileUtils';

const {Dragger} = Upload;
const {Panel} = Collapse;
const {Title, Text} = Typography;

interface FileUploadStepProps {
    selectedFiles: UploadFile<any>[];
    setSelectedFiles: (files: UploadFile<any>[]) => void;
    uploadingFiles: boolean;
    uploadProgress?: number;
    setUploadProgress: (progress: number) => void;
    handleFileUpload: (files: File[]) => Promise<void>;
    handleDeleteFile: (releaseId: string, fileName: string) => Promise<void>;
    fetchAllReleaseFiles: (items: any[]) => Promise<void>;
    fetchingExistingFiles: boolean;
}

// Helper function to format metadata for display in tooltip
const formatMetadata = (metadata?: { [key: string]: any }) => {
    if (!metadata || Object.keys(metadata).length === 0) {
        return <div>No metadata available</div>;
    }

    return (
        <div style={{maxWidth: '300px', maxHeight: '400px', overflow: 'auto'}}>
            <Typography.Title level={5}>File Metadata</Typography.Title>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <tbody>
                {Object.entries(metadata).map(([key, value]) => (
                    <tr key={key} style={{borderBottom: '1px solid #f0f0f0'}}>
                        <td style={{padding: '4px 8px', fontWeight: 'bold', verticalAlign: 'top'}}>
                            {key.replace(/_/g, ' ')}
                        </td>
                        <td style={{padding: '4px 8px', wordBreak: 'break-word'}}>
                            {key === 'duration' ? formatDuration(parseInt(value)) : value}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

// Format duration from nanoseconds to mm:ss.SSS
const formatDuration = (durationNs: number) => {
    // Convert nanoseconds to seconds for calculation
    const totalSeconds = durationNs / 1_000_000_000;

    // Extract whole minutes
    const minutes = Math.floor(totalSeconds / 60);

    // Extract remaining whole seconds
    const seconds = Math.floor(totalSeconds % 60);

    // Extract milliseconds
    const milliseconds = Math.round((totalSeconds % 1) * 1000);

    // Format the result as mm:ss:SSS
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
    const formattedMilliseconds = String(milliseconds).padStart(3, '0');

    return `${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`;
};

const FileUploadStep: React.FC<FileUploadStepProps> = ({
                                                           selectedFiles,
                                                           setSelectedFiles,
                                                           uploadingFiles,
                                                           uploadProgress,
                                                           setUploadProgress,
                                                           handleFileUpload,
                                                           handleDeleteFile,
                                                           fetchAllReleaseFiles,
                                                           fetchingExistingFiles
                                                       }) => {
    const [batchItems] = useAtom(batchItemsAtom);
    const [uploadedFiles] = useAtom(uploadedFilesAtom);
    const [, setCurrentStep] = useAtom(currentStepAtom);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [validationModalVisible, setValidationModalVisible] = useState(false);
    const [selectedFile, setSelectedFile] = useState<any | null>(null);
    const [confirmReplaceModalVisible, setConfirmReplaceModalVisible] = useState(false);
    const [filesToReplace, setFilesToReplace] = useState<{
        file: File,
        releaseId: string,
        trackName: string
    }[]>([]);
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

    // Update upload progress when uploadingFiles changes or when uploadProgress prop changes
    useEffect(() => {
        if (!uploadingFiles) {
            setUploadProgress(0);
        } else if (uploadProgress !== undefined) {
            setUploadProgress(uploadProgress);
        }
    }, [uploadingFiles, uploadProgress]);

    // Function to show validation details for a file
    const showValidationDetails = (file: any) => {
        setSelectedFile(file);
        setValidationModalVisible(true);
    };

    // Function to handle file upload with validation
    const handleUploadWithValidation = (files: File[]) => {
        // Filter out invalid files
        const validFiles: File[] = [];
        const filesToConfirm: File[] = [];

        // Process filenames to strip consecutive spaces
        const processedFiles = files.map(file => stripConsecutiveSpacesInFilename(file));

        let matchedReleaseId = '';
        let matchedTrackName = '';
        for (const file of processedFiles) {
            // Check if the filename exists in any release's metadata
            let isValid = false;
            let isReplacing = false;

            for (const item of batchItems) {
                const release = item.release;
                if (!release) continue;

                // Check if it matches the cover art filename
                if (release.album_cover_filename === file.name) {
                    isValid = true;
                    matchedReleaseId = item.release_id;
                    matchedTrackName = 'Cover Art';

                    // Check if it's replacing an existing file
                    if (uploadedFiles[item.release_id]?.some(f => f.name === file.name)) {
                        isReplacing = true;
                    }
                    break;
                }

                // If no exact match, try to find a match after stripping consecutive spaces from the expected filename
                const processedExpectedCoverName = release.album_cover_filename.replace(/ {2,}/g, ' ');
                if (processedExpectedCoverName === file.name) {
                    isValid = true;
                    matchedReleaseId = item.release_id;
                    matchedTrackName = 'Cover Art';

                    // Check if it's replacing an existing file
                    if (uploadedFiles[item.release_id]?.some(f => f.name === file.name)) {
                        isReplacing = true;
                    }
                    break;
                }

                // Check if it matches any track filename
                for (const track of release.art_tracks || []) {
                    // Check for exact match
                    if (track.filename === file.name) {
                        isValid = true;
                        matchedReleaseId = item.release_id;
                        matchedTrackName = track.title;

                        // Check if it's replacing an existing file
                        if (uploadedFiles[item.release_id]?.some(f => f.name === file.name)) {
                            isReplacing = true;
                        }
                        break;
                    }

                    // If no exact match, try to find a match after stripping consecutive spaces from the expected filename
                    const processedExpectedTrackName = track.filename.replace(/ {2,}/g, ' ');
                    if (processedExpectedTrackName === file.name) {
                        isValid = true;
                        matchedReleaseId = item.release_id;
                        matchedTrackName = track.title;

                        // Check if it's replacing an existing file
                        if (uploadedFiles[item.release_id]?.some(f => f.name === file.name)) {
                            isReplacing = true;
                        }
                        break;
                    }
                }

                if (isValid) break;
            }

            if (!isValid) {
                message.error(`File "${file.name}" does not match any filename in the metadata.`);
            } else if (isReplacing) {
                filesToConfirm.push(file);
            } else {
                validFiles.push(file);
            }
        }

        if (filesToConfirm.length > 0) {
            // If there are files that need confirmation, store them all
            setFilesToUpload([...validFiles]);

            // Collect information about all files that need replacement
            const replacementFiles = [];
            for (const file of filesToConfirm) {
                // Find the matching release and track name for each file
                let fileReleaseId = '';
                let fileTrackName = '';

                for (const item of batchItems) {
                    const release = item.release;
                    if (!release) continue;

                    // Check if it matches the cover art filename
                    if (release.album_cover_filename === file.name) {
                        fileReleaseId = item.release_id;
                        fileTrackName = 'Cover Art';
                        break;
                    }

                    // If no exact match, try to find a match after stripping consecutive spaces from the expected filename
                    const processedExpectedCoverName = release.album_cover_filename.replace(/ {2,}/g, ' ');
                    if (processedExpectedCoverName === file.name) {
                        fileReleaseId = item.release_id;
                        fileTrackName = 'Cover Art';
                        break;
                    }

                    // Check if it matches any track filename
                    for (const track of release.art_tracks || []) {
                        // Check for exact match
                        if (track.filename === file.name) {
                            fileReleaseId = item.release_id;
                            fileTrackName = track.title;
                            break;
                        }

                        // If no exact match, try to find a match after stripping consecutive spaces from the expected filename
                        const processedExpectedTrackName = track.filename.replace(/ {2,}/g, ' ');
                        if (processedExpectedTrackName === file.name) {
                            fileReleaseId = item.release_id;
                            fileTrackName = track.title;
                            break;
                        }
                    }

                    if (fileReleaseId) break;
                }

                replacementFiles.push({
                    file,
                    releaseId: fileReleaseId,
                    trackName: fileTrackName
                });
            }

            setFilesToReplace(replacementFiles);
            setConfirmReplaceModalVisible(true);
        } else if (validFiles.length > 0) {
            // If there are valid files and no confirmations needed, proceed with upload
            handleFileUpload(validFiles);
            setUploadModalVisible(false);
        }
    };

    // Function to handle confirmation of file replacement
    const handleConfirmReplace = () => {
        if (filesToReplace.length > 0) {
            // Add all confirmed files to the upload list
            const filesToReplaceArray = filesToReplace.map(item => item.file);
            const updatedFilesToUpload = [...filesToUpload, ...filesToReplaceArray];
            setFilesToUpload(updatedFilesToUpload);

            // Close the confirmation modal
            setConfirmReplaceModalVisible(false);
            setFilesToReplace([]);

            // Proceed with upload
            handleFileUpload(updatedFilesToUpload);
            setUploadModalVisible(false);
        }
    };

    // Function to handle cancellation of file replacement
    const handleCancelReplace = () => {
        // Close the confirmation modal
        setConfirmReplaceModalVisible(false);
        setFilesToReplace([]);

        // If there are other files to upload, proceed with those
        if (filesToUpload.length > 0) {
            handleFileUpload(filesToUpload);
            setUploadModalVisible(false);
        }
    };

    // Check if any files have been uploaded
    const hasUploadedFiles = Object.values(uploadedFiles).some(files => files && files.length > 0);

    return (
        <div style={{marginTop: 24}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
                <div>
                    <Title level={4}>Upload Audio and Cover Art Files</Title>
                    <Text>Upload the audio files and cover art for all releases. The filenames should match those
                        specified in the Excel file.</Text>
                </div>
                <Space>
                    <Button
                        type="primary"
                        icon={<ReloadOutlined/>}
                        onClick={() => fetchAllReleaseFiles(batchItems)}
                        loading={fetchingExistingFiles}
                        disabled={uploadingFiles}
                    >
                        Refresh Files
                    </Button>
                    {hasUploadedFiles && (
                        <Button
                            type="primary"
                            icon={<UploadOutlined/>}
                            onClick={() => setUploadModalVisible(true)}
                            disabled={uploadingFiles || fetchingExistingFiles}
                        >
                            Upload More
                        </Button>
                    )}
                </Space>
            </div>

            {/* Loading progress bar */}
            {uploadingFiles && (
                <Row style={{marginBottom: 16}}>
                    <Typography.Text>Uploading files...</Typography.Text>
                    <Progress percent={Math.round(uploadProgress)} status="active" style={{width: '100%'}}/>
                </Row>
            )}

            {fetchingExistingFiles && (
                <div style={{textAlign: 'center', margin: '20px 0'}}>
                    <Spin tip="Loading existing files..."/>
                </div>
            )}

            {batchItems.length > 0 && (
                <Collapse defaultActiveKey={batchItems.map(item => item.id)} style={{marginTop: 24}}>
                    {batchItems.map((item) => {
                        const releaseFiles = uploadedFiles[item.release_id] || [];
                        const coverArtFile = releaseFiles.find(file => file.name === item.release?.album_cover_filename);

                        return (
                            <Panel
                                header={
                                    <Space>
                                        <span>{`Release: ${item.release?.name || 'Unnamed Release'}`}</span>
                                    </Space>
                                }
                                key={item.id}
                            >
                                <div style={{marginBottom: 16}}>
                                    <Text strong>Cover Art: </Text>
                                    <Text>{item.release?.album_cover_filename || 'No cover art specified'}</Text>
                                    {item.release?.album_cover_filename && (
                                        <>
                                            {!coverArtFile && <Tag
                                                color={"warning"}
                                                style={{marginLeft: 8}}
                                                icon={<WarningOutlined/>}
                                            >
                                                {"Not Uploaded"}
                                            </Tag>}
                                            {coverArtFile && coverArtFile.metadata && (
                                                <Tooltip
                                                    title={formatMetadata(coverArtFile.metadata)}
                                                    placement="right"
                                                    overlayInnerStyle={{backgroundColor: 'white', color: 'black'}}
                                                >
                                                    <InfoCircleOutlined
                                                        style={{color: '#1890ff', cursor: 'pointer', marginLeft: 8}}/>
                                                </Tooltip>
                                            )}
                                            {coverArtFile && coverArtFile.validationResult && !coverArtFile.validationResult.isValid && (
                                                <>
                                                    <Space>
                                                        <Tag
                                                            color="warning"
                                                            icon={<WarningOutlined/>}
                                                            style={{cursor: 'pointer', marginLeft: 8}}
                                                            onClick={() => showValidationDetails(coverArtFile)}
                                                        >
                                                            {(coverArtFile.validationResult.issues?.length || 0) +
                                                                Object.values(coverArtFile.validationResult?.platformResults || {})
                                                                    .reduce((sum, result: any) => sum + (result.issues?.length || 0), 0)
                                                            } Warning(s)
                                                        </Tag>
                                                        {coverArtFile && (
                                                            <Popconfirm
                                                                title="Delete file"
                                                                onConfirm={() => handleDeleteFile(item.release_id, item.release.album_cover_filename)}
                                                                okText="Yes"
                                                                cancelText="No"
                                                            >
                                                                <Button
                                                                    type="text"
                                                                    danger
                                                                    icon={<DeleteOutlined/>}
                                                                    size="small"
                                                                />
                                                            </Popconfirm>
                                                        )}
                                                    </Space>
                                                    <div style={{marginTop: 8, marginLeft: 8}}>
                                                        {coverArtFile.validationResult.issues?.length > 0 && (
                                                            <div>
                                                                <Typography.Text strong>Technical Validation
                                                                    Issues:</Typography.Text>
                                                                <ul style={{
                                                                    margin: '5px 0 0 0',
                                                                    padding: '0 0 0 20px'
                                                                }}>
                                                                    {coverArtFile.validationResult.issues?.map((issue, index) => (
                                                                        <li key={index}>
                                                                            <Typography.Text type="warning">
                                                                                {issue}
                                                                            </Typography.Text>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                            {coverArtFile && !coverArtFile.validationResult && (
                                                <Popconfirm
                                                    title="Delete file"
                                                    onConfirm={() => handleDeleteFile(item.release_id, item.release.album_cover_filename)}
                                                    okText="Yes"
                                                    cancelText="No"
                                                >
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<DeleteOutlined/>}
                                                        size="small"
                                                        style={{marginLeft: 8}}
                                                    />
                                                </Popconfirm>
                                            )}
                                        </>
                                    )}
                                </div>

                                <Table
                                    dataSource={item.release?.art_tracks || []}
                                    columns={[
                                        {
                                            title: 'Track #',
                                            dataIndex: 'track_number',
                                            key: 'track_number',
                                            width: 80,
                                        },
                                        {
                                            title: 'CD #',
                                            dataIndex: 'volume_number',
                                            key: 'volume_number',
                                            width: 80,
                                            render: (text) => text || 1,
                                        },
                                        {
                                            title: 'Track Title',
                                            dataIndex: 'title',
                                            key: 'title',
                                            render: (text, record) => (
                                                <Text>{text}</Text>
                                            ),
                                        },
                                        {
                                            title: 'Filename',
                                            dataIndex: 'filename',
                                            key: 'filename',
                                        },
                                        {
                                            title: 'Status',
                                            key: 'status',
                                            render: (_, record) => {
                                                const trackFile = releaseFiles.find(file => file.name === record.filename);
                                                return trackFile ? (
                                                    <Space>
                                                        {/*<Tag color="success"*/}
                                                        {/*     icon={<CheckCircleOutlined/>}>Uploaded</Tag>*/}
                                                        {trackFile.metadata && (
                                                            <Tooltip
                                                                title={formatMetadata(trackFile.metadata)}
                                                                placement="right"
                                                                overlayInnerStyle={{
                                                                    backgroundColor: 'white',
                                                                    color: 'black'
                                                                }}
                                                            >
                                                                <InfoCircleOutlined
                                                                    style={{color: '#1890ff', cursor: 'pointer'}}/>
                                                            </Tooltip>
                                                        )}
                                                        {trackFile.validationResult && !trackFile.validationResult.isValid && (
                                                            <>
                                                                <Tag
                                                                    color="warning"
                                                                    icon={<WarningOutlined/>}
                                                                    style={{cursor: 'pointer'}}
                                                                    onClick={() => showValidationDetails(trackFile)}
                                                                >
                                                                    {(trackFile.validationResult.issues?.length || 0) +
                                                                        Object.values(trackFile.validationResult.platformResults || {})
                                                                            .reduce((sum, result: any) => sum + (result.issues?.length || 0), 0)
                                                                    } Warning(s)
                                                                </Tag>
                                                            </>
                                                        )}
                                                    </Space>
                                                ) : (
                                                    <Tag color="warning" icon={<WarningOutlined/>}>Not Uploaded</Tag>
                                                );
                                            },
                                        },
                                        {
                                            title: 'Actions',
                                            key: 'actions',
                                            render: (_, record) => {
                                                const isUploaded = releaseFiles.some(
                                                    file => file.name === record.filename
                                                );
                                                return isUploaded ? (
                                                    <Popconfirm
                                                        title="Delete file"
                                                        onConfirm={() => handleDeleteFile(item.release_id, record.filename)}
                                                        okText="Yes"
                                                        cancelText="No"
                                                    >
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined/>}
                                                        />
                                                    </Popconfirm>
                                                ) : null;
                                            },
                                        },
                                    ]}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    loading={fetchingExistingFiles}
                                />
                            </Panel>
                        );
                    })}
                </Collapse>
            )}

            {/* Only show the dragger if no files have been uploaded yet */}
            {!hasUploadedFiles && !fetchingExistingFiles && (
                <div style={{marginTop: 24}}>
                    <Dragger
                        name="files"
                        multiple={true}
                        beforeUpload={(file, fileList) => {
                            // For the first file in the batch or individual file
                            if (fileList.indexOf(file) === 0) {
                                handleUploadWithValidation(fileList);
                            }
                            return false; // Prevent default upload behavior
                        }}
                        onChange={(info) => {
                            console.log('Upload status changed:', info.file);
                            setSelectedFiles(info.fileList);
                        }}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined/>
                        </p>
                        <p className="ant-upload-text">Click or drag files to this area to upload</p>
                        <p className="ant-upload-hint">
                            Support for multiple files. The filenames should match those specified in the Excel file.
                        </p>
                    </Dragger>
                </div>
            )}

            {/* Upload Modal */}
            <Modal
                open={uploadModalVisible}
                title="Upload Files"
                footer={null}
                onCancel={() => setUploadModalVisible(false)}
                width={700}
            >
                {uploadingFiles ? (
                    <div style={{textAlign: 'center', padding: '50px 0'}}>
                        <Typography.Text>Upload in progress. Please wait...</Typography.Text>
                        <Progress percent={Math.round(uploadProgress)} status="active"
                                  style={{width: '100%', marginTop: 16}}/>
                    </div>
                ) : (
                    <Dragger
                        name="modal_files"
                        multiple={true}
                        beforeUpload={(file, fileList) => {
                            // For the first file in the batch or individual file
                            if (fileList.indexOf(file) === 0) {
                                handleUploadWithValidation(fileList);
                            }
                            return false; // Prevent default upload behavior
                        }}
                        onChange={(info) => {
                            console.log('Upload status changed:', info.file);
                            setSelectedFiles(info.fileList);
                        }}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined/>
                        </p>
                        <p className="ant-upload-text">Click or drag files to this area to upload</p>
                        <p className="ant-upload-hint">
                            Support for multiple files. The filenames should match those specified in the Excel file.
                        </p>
                    </Dragger>
                )}
            </Modal>

            {/* Confirmation Modal for replacing files */}
            <Modal
                open={confirmReplaceModalVisible}
                title="Confirm File Replacement"
                onOk={handleConfirmReplace}
                onCancel={handleCancelReplace}
                okText="Replace All"
                cancelText="Skip All"
            >
                {filesToReplace.length === 1 ? (
                    <>
                        <p>
                            The file <strong>{filesToReplace[0]?.file.name}</strong> will replace an existing file for
                            track <strong>{filesToReplace[0]?.trackName}</strong>.
                        </p>
                        <p>Do you want to replace the existing file?</p>
                    </>
                ) : (
                    <>
                        <p>The following files will replace existing files:</p>
                        <ul>
                            {filesToReplace.map((item, index) => (
                                <li key={index}>
                                    <strong>{item.file.name}</strong> for track <strong>{item.trackName}</strong>
                                </li>
                            ))}
                        </ul>
                        <p>Do you want to replace all existing files?</p>
                    </>
                )}
            </Modal>

            {/* Validation Details Modal */}
            <Modal
                open={validationModalVisible}
                title={selectedFile ? `Validation Results: ${selectedFile.name}` : 'Validation Results'}
                footer={[
                    <Button key="close" onClick={() => setValidationModalVisible(false)}>
                        Close
                    </Button>
                ]}
                onCancel={() => setValidationModalVisible(false)}
                width={700}
            >
                {selectedFile && selectedFile.validationResult && (
                    <div>
                        <div style={{marginBottom: 16}}>
                            <Typography.Title level={5}>Overall Status</Typography.Title>
                            {selectedFile.validationResult.isValid ? (
                                <Tag color="success" icon={<CheckCircleOutlined/>}>Valid</Tag>
                            ) : (
                                <Tag color="warning" icon={<WarningOutlined/>}>Invalid</Tag>
                            )}

                            {selectedFile.validationResult.issues && selectedFile.validationResult.issues.length > 0 && (
                                <div style={{marginTop: 8}}>
                                    <Typography.Text strong>Issues:</Typography.Text>
                                    <ul>
                                        {selectedFile.validationResult.issues.map((issue, index) => (
                                            <li key={index}><Typography.Text type="warning">{issue}</Typography.Text>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <Typography.Title level={5}>Platform-Specific Validation</Typography.Title>
                        <Collapse
                            defaultActiveKey={selectedFile.validationResult.platformResults ? Object.keys(selectedFile.validationResult.platformResults) : []}>
                            {selectedFile.validationResult.platformResults && Object.entries(selectedFile.validationResult.platformResults).map(([platform, result]: [any, any]) => (
                                <Collapse.Panel
                                    key={platform}
                                    header={
                                        <Space>
                                            <span style={{textTransform: 'capitalize'}}>{platform}</span>
                                            {result.isValid ? (
                                                <Tag color="success" icon={<CheckCircleOutlined/>}>Valid</Tag>
                                            ) : (
                                                <Tag color="warning" icon={<WarningOutlined/>}>Invalid</Tag>
                                            )}
                                        </Space>
                                    }
                                >
                                    {result.issues && result.issues.length > 0 ? (
                                        <ul>
                                            {result.issues.map((issue, index) => (
                                                <li key={index}><Typography.Text
                                                    type="warning">{issue}</Typography.Text>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <Typography.Text>No issues found.</Typography.Text>
                                    )}
                                </Collapse.Panel>
                            ))}
                        </Collapse>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default FileUploadStep;
